/** Pure teamwork rules shared by the collab store, the components and the tests. No Vue imports. */
import type { PresenceItem, PresenceState, Note } from '../api/collab/types'
import type { ChatMessage, ContactTagRef, ConversationSummary, TagTone, ThreadItem } from '../api/types'

// ---------------------------------------------------------------- presence

export interface PresenceEntry {
  conversationId: string
  staffId: string
  name: string
  state: Exclude<PresenceState, 'left'>
  /** Local clock (ms) after which the entry is gone unless repeated. */
  expiresAt: number
}

export type PresenceMap = Record<string, PresenceEntry>

const key = (conversationId: string, staffId: string) => `${conversationId}|${staffId}`

/** Apply one presence item heard from the server (expiry counted on this screen's clock, not the server's). */
export function applyPresence(map: PresenceMap, item: PresenceItem, nowMs: number): void {
  const k = key(item.conversation_id, item.staff.id)
  if (item.state === 'left') {
    delete map[k]
    return
  }
  const ttl = Math.min(Math.max(item.ttl, 1), 10)
  map[k] = {
    conversationId: item.conversation_id,
    staffId: item.staff.id,
    name: item.staff.name,
    state: item.state,
    expiresAt: nowMs + ttl * 1000,
  }
}

/** Colleagues in one chat right now (never me), typing first. */
export function othersIn(map: PresenceMap, conversationId: string, meId: string | null, nowMs: number): PresenceEntry[] {
  return Object.values(map)
    .filter((e) => e.conversationId === conversationId && e.staffId !== meId && e.expiresAt > nowMs)
    .sort((a, b) => (a.state === b.state ? a.name.localeCompare(b.name) : a.state === 'typing' ? -1 : 1))
}

export function pruneExpired(map: PresenceMap, nowMs: number): void {
  for (const [k, e] of Object.entries(map)) if (e.expiresAt <= nowMs) delete map[k]
}

/** "ศิริกำลังพิมพ์ตอบอยู่" · "จิต และ พลอย กำลังดูแชทนี้" */
export function presenceText(entries: PresenceEntry[]): string | null {
  if (!entries.length) return null
  const typing = entries.filter((e) => e.state === 'typing')
  const names = (xs: PresenceEntry[]) => (xs.length <= 2 ? xs.map((e) => e.name).join(' และ ') : `${xs[0]!.name} และอีก ${xs.length - 1} คน`)
  if (typing.length) return `${names(typing)}กำลังพิมพ์ตอบอยู่`
  return `${names(entries)} กำลังดูแชทนี้`
}

// ---------------------------------------------------------------- collision guard

export const COLLISION_MS = 30000

export interface Collision {
  /** Who replied: a colleague's name, "AI", or "มีคนตอบจากแอปอื่น". */
  who: string
  at: string
}

/**
 * Someone else answered this customer in the last 30 s (a colleague, the AI, or a reply from the LINE OA app /
 * Page inbox): the staff member is asked before sending on top of it.
 */
export function recentOtherReply(
  items: readonly ThreadItem[],
  meId: string | null,
  nowMs: number,
  windowMs = COLLISION_MS,
): Collision | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const m = items[i]
    if (!m || m.kind !== 'message') continue
    const age = nowMs - Date.parse(m.at)
    if (age > windowMs) return null
    if (m.direction !== 'out' || m.id.startsWith('tmp-')) continue
    if (m.send?.status === 'failed') continue
    const a = (m as ChatMessage).author
    if (a.kind === 'staff' && a.staff.id !== meId) return { who: a.staff.name, at: m.at }
    if (a.kind === 'ai') return { who: 'AI', at: m.at }
    // the out-of-hours notice is not an answer: the customer's question is still open (api/app/out_of_hours.py)
    if (a.kind === 'auto' && a.reply === false) continue
    if (a.kind === 'auto') return { who: 'ระบบตอบอัตโนมัติ', at: m.at }
    if (a.kind === 'page') return { who: 'มีคนตอบจากแอปอื่น', at: m.at }
  }
  return null
}

export function collisionText(c: Collision, nowMs: number): string {
  const sec = Math.max(0, Math.round((nowMs - Date.parse(c.at)) / 1000))
  const when = sec < 5 ? 'เมื่อกี้' : `${sec} วินาทีที่แล้ว`
  if (c.who === 'มีคนตอบจากแอปอื่น') return `มีคนตอบลูกค้าจากแอปอื่น${when} ส่งต่อไหม`
  return `${c.who}เพิ่งตอบไป${when} ส่งต่อไหม`
}

// ---------------------------------------------------------------- tags

export const TONES: { value: TagTone; label: string }[] = [
  { value: 'crit', label: 'แดง' },
  { value: 'warn', label: 'ส้ม' },
  { value: 'ok', label: 'เขียว' },
  { value: 'info', label: 'ฟ้า' },
  { value: 'violet', label: 'ม่วง' },
  { value: 'neutral', label: 'เทา' },
]

export function matchesTag(c: ConversationSummary, tagId: string | null): boolean {
  return !tagId || !!c.tags?.some((t) => t.id === tagId)
}

/** Tag chips on an inbox row (owner 18 ต.ค. 69 "ให้ติด tag รอโอน ด้วย และมี tag สำคัญต่างๆ"). */
export const ROW_TAGS = 2

/**
 * The row's tag chips: the customer's most important labels (the API sends them most important first — the team's order
 * in ตั้งค่า › แท็กลูกค้า), at most `max`, the rest as "+N". A row without `tags` (older data) shows its one `tag`.
 */
export function rowChips(c: ConversationSummary, max: number = ROW_TAGS): { shown: ContactTagRef[]; more: ContactTagRef[] } {
  const all: ContactTagRef[] = c.tags?.length ? c.tags : c.tag ? [{ id: `row:${c.tag.label}`, name: c.tag.label, tone: c.tag.kind }] : []
  const n = Math.max(0, Math.floor(max))
  return { shown: all.slice(0, n), more: all.slice(n) }
}

// ---------------------------------------------------------------- notes in the timeline

/**
 * Notes written in this chat, inside the loaded window of the thread (older ones are in the customer tab).
 * `hasOlder` = older messages exist but are not loaded yet; either way a note never sits above the first
 * loaded item (a chat's first message is always older than any note written in it).
 */
export function notesInThread(notes: readonly Note[], conversationId: string, items: readonly ThreadItem[], hasOlder: boolean): Note[] {
  const first = items[0]?.at ?? null
  if (first === null && hasOlder) return []
  return notes
    .filter((n) => n.conversation_id === conversationId && (first === null || n.at >= first))
    .slice()
    .sort((a, b) => a.at.localeCompare(b.at))
}

/** Seconds → "เงียบไป 2 ชม." style text for the follow-up card. */
export function silentText(sinceIso: string | null, nowMs: number): string | null {
  if (!sinceIso) return null
  const min = Math.max(0, Math.floor((nowMs - Date.parse(sinceIso)) / 60000))
  if (min < 60) return `${min} นาที`
  if (min < 1440) return `${Math.floor(min / 60)} ชม.`
  return `${Math.floor(min / 1440)} วัน`
}
